import React, { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import {
  Button, IconButton, CircularProgress,
  TextField, InputAdornment, Tooltip,
  Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TablePagination,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Alert,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckIcon from "@mui/icons-material/Check";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { TEAL, TEAL_LIGHT } from "../constants";
import { BootstrapButton } from "../components/BootstrapButton";
import ConfirmModal from "../components/ConfirmModal";

const DEFAULT_PAGE_SIZE = 10;
const PAGE_SIZE_OPTIONS = [10, 25, 50];

const TH = {
  fontWeight: 600,
  backgroundColor: TEAL,
  color: "#fff",
  fontSize: 13,
  border: "1px solid rgba(200,200,200,0.5)",
};

export default function CatalogListPage({ apiFetch, onSelectCatalog, onOpenGenerate, searchBarSlot }) {
  const [list, setList] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [loading, setLoading] = useState(false);

  // ── Create dialog ──────────────────────────────────────────────────────────
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [adding, setAdding] = useState(false);

  // ── Copy dialog ────────────────────────────────────────────────────────────
  const [copyDialog, setCopyDialog] = useState({ open: false, report: null, name: "", loading: false });

  // ── Inline rename ──────────────────────────────────────────────────────────
  const [renamingId, setRenamingId] = useState(null);
  const [renameVal, setRenameVal] = useState("");
  const [renamingLoading, setRenamingLoading] = useState(false);

  // ── Delete confirm ─────────────────────────────────────────────────────────
  const [confirmDelete, setConfirmDelete] = useState({ open: false, report: null, loading: false });

  // ── Fetch ──────────────────────────────────────────────────────────────────
  const fetchPage = useCallback(async (p, size = pageSize) => {
    setLoading(true);
    try {
      const r = await apiFetch.get(`/api/v1/catalog-report/reports?page=${p + 1}&size=${size}`);
      setList(r?.data || []);
      setTotal(r?.pagination_info?.total || 0);
    } catch {
      setList([]); setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [apiFetch, pageSize]);

  useEffect(() => { fetchPage(0, pageSize); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePageChange = (_, newPage) => { setPage(newPage); fetchPage(newPage, pageSize); };

  const handlePageSizeChange = (e) => {
    const newSize = parseInt(e.target.value, 10);
    setPageSize(newSize);
    setPage(0);
    fetchPage(0, newSize);
  };

  // ── Create ─────────────────────────────────────────────────────────────────
  const handleOpenCreate = () => { setNewName(""); setCreateOpen(true); };
  const handleCloseCreate = () => { if (!adding) setCreateOpen(false); };

  const handleAdd = async () => {
    if (!newName.trim()) return;
    setAdding(true);
    try {
      const res = await apiFetch.post("/api/v1/catalog-report/reports", { catalog_name: newName.trim() });
      const id = res?.data?.catalog_id ?? res?.catalog_id ?? res?.data?.id ?? res?.id ?? null;
      const name = newName.trim();
      setCreateOpen(false);
      setNewName("");
      setPage(0);
      await fetchPage(0, pageSize);
      if (id) {
        setTimeout(() => onSelectCatalog(id, name), 100);
      }
    } catch (e) {
      const backendDetail = e?.response?.data?.detail || e?.data?.detail || e?.detail || e?.message || "Gagal membuat catalog";
      toast.error(backendDetail);
    }
    finally { setAdding(false); }
  };

  // ── Copy ───────────────────────────────────────────────────────────────────
  const handleOpenCopy = (report) => {
    setCopyDialog({ open: true, report, name: `${report.catalog_name}`, loading: false });
  };

  const handleCloseCopy = () => {
    if (copyDialog.loading) return;
    setCopyDialog({ open: false, report: null, name: "", loading: false });
  };

  const handleToastRefresh = async () => {
    setPage(0);
    await fetchPage(0, pageSize);
  };

  const handleSubmitCopy = async () => {
    if (!copyDialog.name.trim() || !copyDialog.report) return;
    const targetName = copyDialog.name.trim();
    setCopyDialog((p) => ({ ...p, loading: true }));
    try {
      const res = await apiFetch.post("/api/v1/catalog-report/reports/copy", {
        refer_catalog_id: String(copyDialog.report.catalog_id),
        new_catalog_name: targetName,
      });
      setCopyDialog({ open: false, report: null, name: "", loading: false });

      const backendMessage = res?.message || "Catalog copy started in background.";
      toast(({ closeToast }) => (
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{ fontSize: 14, lineHeight: 1.4 }}>
            {backendMessage} — klik refresh untuk melihat "{targetName}".
          </span>
          <button
            onClick={() => { handleToastRefresh(); closeToast(); }}
            style={{
              border: "none", background: "#0ea5e9", color: "#fff",
              fontWeight: 700, cursor: "pointer", fontSize: 14,
              padding: "6px 14px", borderRadius: 6, flexShrink: 0,
              whiteSpace: "nowrap",
            }}
          >
            Refresh
          </button>
        </div>
      ), { autoClose: 8000 });
    } catch (e) {
      // Ambil pesan error dari backend `detail` (mis. nama duplikat), fallback ke pesan generic.
      const backendDetail =
        e?.response?.data?.detail || e?.data?.detail || e?.detail || e?.message || "Gagal meng-copy catalog";
      toast.error(backendDetail);
      // Dialog TETAP terbuka & loading di-reset, biar user bisa langsung perbaiki nama tanpa buka ulang dialog.
      setCopyDialog((p) => ({ ...p, loading: false }));
    }
  };

  // ── Rename ─────────────────────────────────────────────────────────────────
  const handleRename = async (report) => {
    if (!renameVal.trim() || renameVal.trim() === report.catalog_name) { setRenamingId(null); return; }
    setRenamingLoading(true);
    try {
      await apiFetch.put(`/api/v1/catalog-report/reports/${report.catalog_id}`, { catalog_name: renameVal.trim() });
      setRenamingId(null);
      await fetchPage(page, pageSize);
    } catch (e) {
      const backendDetail = e?.response?.data?.detail || e?.data?.detail || e?.detail || e?.message || "Gagal mengubah nama";
      toast.error(backendDetail);
    }
    finally { setRenamingLoading(false); }
  };

  // ── Delete ─────────────────────────────────────────────────────────────────
  const handleConfirmDelete = async () => {
    setConfirmDelete((p) => ({ ...p, loading: true }));
    try {
      await apiFetch.delete(`/api/v1/catalog-report/reports/${confirmDelete.report.catalog_id}`);
      setConfirmDelete({ open: false, report: null, loading: false });
      const newTotal = total - 1;
      const maxPage = Math.max(0, Math.ceil(newTotal / pageSize) - 1);
      const targetPage = page > maxPage ? maxPage : page;
      setPage(targetPage);
      await fetchPage(targetPage, pageSize);
    } catch (e) {
      const backendDetail = e?.response?.data?.detail || e?.data?.detail || e?.detail || e?.message || "Gagal menghapus catalog";
      toast.error(backendDetail);
      setConfirmDelete((p) => ({ ...p, loading: false }));
    }
  };

  // ── Generate (per-row) ────────────────────────────────────────────────────
  const handleGenerate = async (report) => {
    await onOpenGenerate(report.catalog_id, report.catalog_name);
    await fetchPage(page, pageSize);
  };

  // ──────────────────────────────────────────────────────────────────────────
  return (
    <>
      {/* ── Top bar ─────────────────────────────────────────────────────────── */}
      <div style={{
        display: "flex", alignItems: "center", gap: 10,
        marginBottom: 20, flexWrap: "wrap",
        justifyContent: "space-between",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          {searchBarSlot}
          <Button
            disableRipple variant="contained" onClick={handleOpenCreate}
            sx={{
              height: 38, textTransform: "none", fontSize: 13, boxShadow: "none",
              backgroundColor: TEAL, borderRadius: "8px",
              "&:hover": { backgroundColor: "#058a9a", boxShadow: "none" },
            }}
          >
            <AddIcon sx={{ fontSize: 16, mr: 0.6 }} />
            Add Catalog
          </Button>
        </div>
      </div>

      {/* ── Table ──────────────────────────────────────────────────────────── */}
      <Paper sx={{ width: "100%", overflow: "hidden", border: "1px solid #e0e0e0", borderRadius: 2 }}>
        <TableContainer>
          <Table
            size="small"
            aria-label="catalog table"
            sx={{ border: 1, borderColor: "grey.300", borderCollapse: "collapse", tableLayout: "fixed" }}
          >
            <TableHead>
              <TableRow>
                <TableCell style={TH} align="center" sx={{ width: 110 }}>Id Catalog</TableCell>
                <TableCell style={TH} sx={{ width: "calc((100% - 110px) / 2)" }}>Nama Catalog</TableCell>
                <TableCell style={TH} align="center" sx={{ width: "calc((100% - 110px) / 2)" }}>Aksi</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && list.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} align="center" sx={{ py: 6, border: 1, borderColor: "grey.300" }}>
                    <CircularProgress size={24} sx={{ color: TEAL }} />
                  </TableCell>
                </TableRow>
              ) : list.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} align="center"
                    sx={{ py: 6, color: "#9ca3af", fontSize: 13, border: 1, borderColor: "grey.300" }}>
                    Belum ada catalog. Klik "Buat Catalog" untuk memulai.
                  </TableCell>
                </TableRow>
              ) : list.map((r) => (
                <TableRow key={r.catalog_id} hover
                  sx={{ "&:hover": { backgroundColor: `${TEAL_LIGHT}66` } }}>

                  <TableCell align="center"
                    sx={{ border: 1, borderColor: "grey.300", fontFamily: "monospace", fontSize: 11.5, color: "#6b7280", whiteSpace: "nowrap" }}>
                    {r.catalog_id}
                  </TableCell>

                  <TableCell sx={{ border: 1, borderColor: "grey.300" }}>
                    {renamingId === r.catalog_id ? (
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <TextField
                          size="small" value={renameVal} autoFocus
                          onChange={(e) => setRenameVal(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") handleRename(r);
                            if (e.key === "Escape") setRenamingId(null);
                          }}
                          sx={{ flex: 1, "& .MuiInputBase-input": { fontSize: 13, py: 0.5 } }}
                        />
                        <IconButton size="small" onClick={() => handleRename(r)} disabled={renamingLoading}
                          sx={{ color: TEAL, border: `1px solid ${TEAL}`, borderRadius: 1, p: 0.4 }}>
                          {renamingLoading
                            ? <CircularProgress size={13} sx={{ color: TEAL }} />
                            : <CheckIcon fontSize="small" />}
                        </IconButton>
                        <IconButton size="small" onClick={() => setRenamingId(null)}
                          sx={{ color: "#999", border: "1px solid #ddd", borderRadius: 1, p: 0.4 }}>
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </div>
                    ) : (
                      <span style={{ fontSize: 13, color: "#1f2937", fontWeight: 500 }}>{r.catalog_name}</span>
                    )}
                  </TableCell>

                  <TableCell align="center" sx={{ border: 1, borderColor: "grey.300" }}>
                    {renamingId !== r.catalog_id && (
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-start", gap: 6, flexWrap: "nowrap" }}>
                        <Button
                          size="small" variant="outlined"
                          onClick={() => handleOpenCopy(r)}
                          startIcon={<ContentCopyIcon sx={{ fontSize: 14 }} />}
                          sx={{
                            textTransform: "none", fontSize: 12, py: 0.5, px: 1.5,
                            borderColor: "#f57c00", color: "#f57c00", borderRadius: "6px",
                            whiteSpace: "nowrap", minWidth: "auto",
                            "&:hover": { backgroundColor: "#fff3e0", borderColor: "#f57c00" },
                          }}
                        >
                          Copy
                        </Button>

                        {r.is_custom && (
                          <Button
                            size="small" variant="outlined"
                            onClick={() => handleGenerate(r)}
                            sx={{
                              textTransform: "none", fontSize: 12, py: 0.5, px: 1.5,
                              borderColor: "#7c3aed", color: "#7c3aed", borderRadius: "6px",
                              whiteSpace: "nowrap", minWidth: "auto",
                              "&:hover": { backgroundColor: "#f5f3ff", borderColor: "#7c3aed" },
                            }}
                          >
                            Generate Hierarchy
                          </Button>
                        )}
                        <Button
                          size="small" variant="outlined"
                          onClick={() => onSelectCatalog(r.catalog_id, r.catalog_name)}
                          startIcon={<AccountTreeIcon sx={{ fontSize: 14 }} />}
                          sx={{
                            textTransform: "none", fontSize: 12, py: 0.5, px: 1.5,
                            borderColor: TEAL, color: TEAL, borderRadius: "6px",
                            whiteSpace: "nowrap", minWidth: "auto",
                            "&:hover": { backgroundColor: TEAL_LIGHT, borderColor: TEAL },
                          }}
                        >
                          View Hierarchy
                        </Button>

                        <Tooltip title="Ubah nama">
                          <IconButton size="small"
                            onClick={() => { setRenamingId(r.catalog_id); setRenameVal(r.catalog_name); }}
                            sx={{ color: "#bbb", "&:hover": { color: TEAL } }}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Hapus catalog">
                          <IconButton size="small"
                            onClick={() => setConfirmDelete({ open: true, report: r, loading: false })}
                            sx={{ color: "#bbb", "&:hover": { color: "#e53935" } }}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {total > 0 && (
          <TablePagination
            rowsPerPageOptions={PAGE_SIZE_OPTIONS}
            component="div"
            count={total}
            rowsPerPage={pageSize}
            page={page}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handlePageSizeChange}
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} dari ${count} catalog`}
            labelRowsPerPage="Baris per halaman:"
          />
        )}
      </Paper>

      {/* ── Dialog Buat Catalog ─────────────────────────────────────────────── */}
      <Dialog open={createOpen} onClose={handleCloseCreate} fullWidth maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ fontSize: 15, fontWeight: 700, pb: 1 }}>
          Add Catalog
        </DialogTitle>
        <DialogContent sx={{ pt: "8px !important" }}>
          <TextField
            autoFocus fullWidth size="small"
            placeholder="Masukkan nama catalog..."
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleAdd()}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <AddIcon sx={{ fontSize: 15, color: "#bbb" }} />
                </InputAdornment>
              ),
            }}
            sx={{ "& .MuiInputBase-input": { fontSize: 13 } }}
          />
          <div style={{
            display: "flex", alignItems: "flex-start", gap: 6,
            marginTop: 8, fontSize: 11.5, color: "#8a8a8a", lineHeight: 1.4,
          }}>
            <InfoOutlinedIcon sx={{ fontSize: 14, mt: "1px", flexShrink: 0 }} />
            <span>
              Gunakan nama yang unik &amp; deskriptif (mis. sertakan periode/kategori) supaya mudah dikenali di daftar nanti. Sistem tidak mengecek nama duplikat secara otomatis.
            </span>
          </div>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={handleCloseCreate} disabled={adding}
            sx={{ textTransform: "none", fontSize: 13, color: "#6b7280" }}>
            Batal
          </Button>
          <BootstrapButton
            onClick={handleAdd} disabled={adding || !newName.trim()}
            sx={{ minWidth: 120, fontSize: 13 }}
          >
            {adding ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : "+ Add Catalog"}
          </BootstrapButton>
        </DialogActions>
      </Dialog>

      {/* ── Dialog Copy Catalog ── */}
      <Dialog open={copyDialog.open} onClose={handleCloseCopy} fullWidth maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ fontSize: 15, fontWeight: 700, pb: 1 }}>
          Copy Catalog
        </DialogTitle>
        <DialogContent sx={{ pt: "8px !important" }}>
          <div style={{ fontSize: 12.5, color: "#666", marginBottom: 10 }}>
            Menyalin dari <b>{copyDialog.report?.catalog_name}</b>. Semua reportline & custom filter
            akan ikut ter-copy ke catalog baru (proses berjalan di background).
          </div>

          <TextField
            autoFocus fullWidth size="small"
            placeholder="Nama catalog baru..."
            value={copyDialog.name}
            onChange={(e) => setCopyDialog((p) => ({ ...p, name: e.target.value }))}
            onKeyDown={(e) => e.key === "Enter" && handleSubmitCopy()}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <ContentCopyIcon sx={{ fontSize: 14, color: "#bbb" }} />
                </InputAdornment>
              ),
            }}
            sx={{ "& .MuiInputBase-input": { fontSize: 13 } }}
          />

          <Alert severity="warning" variant="outlined" sx={{ mt: 1.5, fontSize: 12, py: 0.5 }}>
            Pastikan nama catalog baru berbeda dari catalog referensi maupun catalog lain yang sudah ada.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={handleCloseCopy} disabled={copyDialog.loading}
            sx={{ textTransform: "none", fontSize: 13, color: "#6b7280" }}>
            Batal
          </Button>
          <BootstrapButton
            onClick={handleSubmitCopy} disabled={copyDialog.loading || !copyDialog.name.trim()}
            sx={{ minWidth: 120, fontSize: 13, backgroundColor: "#f57c00", "&:hover": { backgroundColor: "#e65100" } }}
          >
            {copyDialog.loading ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : "Copy Catalog"}
          </BootstrapButton>
        </DialogActions>
      </Dialog>

      {/* ── Confirm Delete ─────────────────────────────────────────────────── */}
      <ConfirmModal
        open={confirmDelete.open}
        title="Hapus Catalog?"
        message={`Catalog "${confirmDelete.report?.catalog_name}" beserta semua baris dan produk akan dihapus permanen.`}
        warningText="Tindakan ini tidak dapat dibatalkan."
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmDelete({ open: false, report: null, loading: false })}
        loading={confirmDelete.loading}
        confirmLabel="Ya, Hapus"
        confirmColor="#e53935"
      />
    </>
  );
}
